/**
 * "Added to cart" popup (snippets/atc-popup.liquid).
 *
 * - Opens on cart:added (dispatched by cart-service.js for every add) with the
 *   added line item: image, title, variant, price.
 * - Reads /cart.js for the "View cart (N)" count and the free-shipping message.
 * - "View cart" opens the cart drawer when it's on the page, otherwise follows the link.
 * - Closes on the X, Escape, or a click outside.
 */
// Same shape as Liquid's `money` output (£1,234.00) — utilities.formatMoney skips the thousands separator.
function formatMoney(cents) {
  const symbol = (window.umbrellaConfig && window.umbrellaConfig.currency && window.umbrellaConfig.currency.symbol) || '£';
  const amount = (cents / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${symbol}${amount}`;
}

class RcAtcPopup extends HTMLElement {

  constructor() {
    super();
    this.closeTimer = null;
    this.returnFocusTo = null;
  };

  connectedCallback() {
    const events = (window.umbrellaConfig && window.umbrellaConfig.events) || {};
    this.addedEvent = events.cartAdded || 'cart:added';
    this.qcOpenEvent = events.qcOpen || 'quickCart:open';

    this.els = {
      image: this.querySelector('[data-atc-image]'),
      title: this.querySelector('[data-atc-title]'),
      variant: this.querySelector('[data-atc-variant]'),
      price: this.querySelector('[data-atc-price]'),
      count: this.querySelector('[data-atc-count]'),
      viewCart: this.querySelector('[data-atc-view-cart]'),
      shipping: this.querySelector('[data-atc-shipping]'),
      shippingText: this.querySelector('[data-atc-shipping-text]'),
      close: this.querySelector('[data-atc-close]'),
    };

    document.addEventListener(this.addedEvent, this.onAdded);
    if (this.els.close) this.els.close.addEventListener('click', this.close);
    if (this.els.viewCart) this.els.viewCart.addEventListener('click', this.onViewCart);
  };

  disconnectedCallback() {
    document.removeEventListener(this.addedEvent, this.onAdded);
    this.unbindDismiss();
  };

  onAdded = (event) => {
    const item = event && event.detail && event.detail.item;
    if (!item) return;
    this.fill(item);
    this.open();
    this.refreshCart();
  };

  fill(item) {
    const { image, title, variant, price } = this.els;
    if (image) {
      if (item.image) {
        image.src = this.sizedImage(item.image, 280);
        image.alt = item.product_title || '';
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }
    if (title) title.textContent = item.product_title || item.title || '';
    if (variant) variant.textContent = item.product_has_only_default_variant ? '' : (item.variant_title || '');
    if (price) price.textContent = formatMoney(item.final_price != null ? item.final_price : item.price);
  };

  sizedImage(url, width) {
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  async refreshCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (this.els.count) this.els.count.textContent = String(cart.item_count);
      this.renderShipping(cart.total_price);
    } catch (e) {
      // Count/shipping just keep their last values.
    }
  };

  renderShipping(total) {
    const { shipping, shippingText } = this.els;
    if (!shipping || !shippingText) return;
    const threshold = parseInt(this.dataset.shippingThreshold || '0', 10);
    if (!threshold) return;
    const remaining = threshold - total;
    if (remaining <= 0) {
      shippingText.textContent = this.dataset.shippingQualifiedText || '';
    } else {
      shippingText.textContent = (this.dataset.shippingText || '').replace('%amount%', formatMoney(remaining));
    }
    shipping.hidden = !shippingText.textContent;
  };

  onViewCart = (event) => {
    if (!document.querySelector('rc-quick-cart')) return; // no drawer on this page → follow the link
    event.preventDefault();
    this.close({ restoreFocus: false });
    document.dispatchEvent(new CustomEvent(this.qcOpenEvent));
  };

  open() {
    if (this.hidden) this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.positionBelowHeader();
    this.hidden = false;
    requestAnimationFrame(() => {
      this.dataset.open = 'true';
      if (this.els.close) this.els.close.focus({ preventScroll: true });
    });
    this.bindDismiss();
  };

  // Sit just under the header wherever it currently ends (announcement bar shown or scrolled away).
  positionBelowHeader() {
    const header = document.querySelector('header');
    const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    if (bottom) this.style.top = `${bottom + 8}px`;
    else this.style.removeProperty('top');
  };

  close = ({ restoreFocus = true } = {}) => {
    if (this.hidden) return;
    this.dataset.open = 'false';
    this.unbindDismiss();
    window.clearTimeout(this.closeTimer);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.closeTimer = window.setTimeout(() => { this.hidden = true; }, reduceMotion ? 0 : 200);
    if (restoreFocus && this.returnFocusTo && this.returnFocusTo.isConnected) this.returnFocusTo.focus({ preventScroll: true });
    this.returnFocusTo = null;
  };

  bindDismiss() {
    window.clearTimeout(this.closeTimer);
    // Deferred so the click that triggered the add doesn't immediately count as "outside".
    window.setTimeout(() => document.addEventListener('click', this.onDocumentClick), 0);
    document.addEventListener('keydown', this.onKeydown);
  };

  unbindDismiss() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  };

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.close({ restoreFocus: false });
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') this.close();
  };

};

if (!customElements.get('rc-atc-popup')) customElements.define('rc-atc-popup', RcAtcPopup);
